import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Label,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import type { ChannelGroup } from '../../model';
import {
  channelGroupDialogSchema,
  type ChannelGroupDialogFormValue,
} from './ChannelGroupDialog.schema';

interface ChannelGroupDialogProps {
  onClose: () => void;
  onCreate: (channelGroup: ChannelGroup) => void;
}

export function ChannelGroupDialog({ onClose, onCreate }: ChannelGroupDialogProps) {
  const inputId = React.useId();
  const errorId = `${inputId}-error`;
  const form = useForm<ChannelGroupDialogFormValue>({
    defaultValues: { name: '' },
    resolver: zodResolver(channelGroupDialogSchema),
  });
  const nameError = form.formState.errors.name?.message;

  function submit(values: ChannelGroupDialogFormValue) {
    onCreate({
      name: values.name,
      channelsCount: 0,
      updatedAt: new Date().toISOString(),
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-[520px] gap-0 overflow-hidden p-0">
        <DialogHeader className="border-border border-b px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">
            {dashboardT('channelGroups.dialog.title')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {dashboardT('channelGroups.dialog.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={(event) => void form.handleSubmit(submit)(event)} noValidate>
            <div className="space-y-1.5 px-5 py-5">
              <Label className="text-[13px] font-semibold" htmlFor={inputId}>
                {dashboardT('channelGroups.dialog.name')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id={inputId}
                maxLength={80}
                required
                aria-describedby={nameError ? errorId : undefined}
                aria-invalid={Boolean(nameError)}
                placeholder={dashboardT('channelGroups.dialog.namePlaceholder')}
                {...form.register('name')}
              />
              {nameError ? (
                <p id={errorId} className="text-destructive text-xs" role="alert">
                  {nameError}
                </p>
              ) : null}
            </div>
            <DialogFooter className="border-border border-t px-5 py-3">
              <Button type="button" variant="outline" onClick={onClose}>
                {dashboardT('channelGroups.dialog.cancel')}
              </Button>
              <Button type="submit">{dashboardT('channelGroups.dialog.submit')}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
